// v2/pages/AccountV2.js — Hesabim
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, FileDown, FileSpreadsheet, TrendingDown, TrendingUp,
  Briefcase, Inbox, KeyRound, LifeBuoy, Building2, Hash, Clock,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ToastStack from '../components/Toast';
import useToast from '../hooks/useToast';
import useMenuNav from '../hooks/useMenuNav';
import useCartContext from '../hooks/CartContext';
import useExchangeRates from '../hooks/useExchangeRates';
import useProfile from '../hooks/useProfile';
import useAccountBalance from '../hooks/useAccountBalance';
import useCari from '../hooks/useCari';
import { useDebtTransactions } from '../hooks/useDebt';
import { formatTRY, formatNumber } from '../utils/format';
import styles from '../styles/DashboardV2.module.css';
import page from '../styles/AccountV2.module.css';
import '../styles/tokens.css';


const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

export default function AccountV2() {
  const [searchTerm, setSearchTerm] = useState('');
  const [term, setTerm] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const navigate = useNavigate();
  const cart = useCartContext();
  const toast = useToast();
  const kurlar = useExchangeRates();
  const EUR_RATE = kurlar.eurRate;

  const handleMenu = useMenuNav(toast.push);

  // Hesap hareketleri gercek veriden gelir; arama/tarih/sayfalama sunucu tarafli.
  const [sayfa, setSayfa] = useState(1);
  const filtre = useMemo(
    () => ({ q: term, from, to, type: '', page: sayfa, per: 25 }),
    [term, from, to, sayfa]
  );
  const hareket = useDebtTransactions(filtre);
  const rows = hareket.items;
  const totals = hareket.stats;

  // filtre degisince ilk sayfaya don
  useEffect(() => { setSayfa(1); }, [term, from, to]);

  // Bakiye, profil ve cari bilgisi gercek veriden gelir.
  const bakiye = useAccountBalance();
  const profilDurum = useProfile();
  const cari = useCari();
  const pr = profilDurum.profil || {};
  const b = bakiye.balance;

  const s = useMemo(() => {
    if (!b) {
      return { currency: 'TL', status: 'balanced', statusLabel: '—',
               currentBalance: 0, totalDebt: 0, totalCredit: 0 };
    }
    return {
      currency: 'TL',
      status: b.borclu ? 'debtor' : (b.signedBalance < 0 ? 'creditor' : 'balanced'),
      statusLabel: b.borclu ? 'Borçlu' : (b.signedBalance < 0 ? 'Alacaklı' : 'Dengede'),
      currentBalance: b.balance,
      totalDebt: b.totalDebt,
      totalCredit: b.totalCredit,
    };
  }, [b]);

  const p = useMemo(() => ({
    displayName: pr.ad_soyad || cari.adSoyad || '—',
    role: pr.gorev || pr.meslek || '—',
    customerCode: cari.kod || '—',
    companyTitle: cari.unvan || '—',
    fields: [
      { key: 'fullName',  label: 'Ad Soyad',     value: pr.ad_soyad || null },
      { key: 'email',     label: 'E-posta',      value: pr.mail || null, type: 'email' },
      { key: 'role',      label: 'Görev',        value: pr.gorev || null },
      { key: 'job',       label: 'Meslek',       value: pr.meslek || null },
      { key: 'mobile',    label: 'Cep Telefonu', value: pr.cep_telefonu || null, type: 'tel' },
      { key: 'extension', label: 'Dahili No',    value: pr.dahili_no || null },
      { key: 'birthDate', label: 'Doğum Tarihi',
        value: pr.dogum_tarihi ? fmtDate(pr.dogum_tarihi) : null },
    ],
  }), [pr, cari]);

  const initial = (p.displayName || '?').trim().charAt(0).toLocaleUpperCase('tr');

  const notReady = (what) => toast.push(`${what} dışa aktarımı yakında eklenecek.`, 'info');

  return (
    <div className={styles.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <Header
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        cart={cart}
        eurRate={EUR_RATE}
        onMenuSelect={handleMenu}
      />

      <main className={styles.main}>
        <div className={page.head}>
          <h1 className={page.title}>
            Hesabım
            <span className={page.titleSub}>Cari bilgileri ve hesap hareketleri</span>
          </h1>
        </div>

        {/* ust serit */}
        <div className={page.topGrid}>
          <section className={page.balCard} aria-label="Bakiye özeti">
            <div className={page.balTop}>
              <span className={page.curChip}>{s.currency}</span>
              <span className={`${page.statChip} ${s.status === 'debtor' ? page.statDebtor : page.statCreditor}`}>
                {s.status === 'debtor' ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
                {s.statusLabel}
              </span>
            </div>
            <div className={page.balRow}>
              <div className={page.balItem}>
                <span className={page.balLabel}>Güncel Bakiye</span>
                <span className={page.balValue}>{formatTRY(s.currentBalance)}</span>
              </div>
              <div className={page.balItem}>
                <span className={page.balLabel}>Toplam Borç</span>
                <span className={page.balValueSm}>{formatTRY(s.totalDebt)}</span>
              </div>
              <div className={page.balItem}>
                <span className={page.balLabel}><Clock size={9} /> Son Hareket</span>
                <span className={page.balValueSm}>{b ? b.lastActivity : '—'}</span>
              </div>
            </div>
          </section>

          <section className={page.userCard} aria-label="Kullanıcı">
            <div className={page.userTop}>
              <span className={page.avatar}>{initial}</span>
              <div className={page.userInfo}>
                <span className={page.userName}>{p.displayName}</span>
                <span className={page.userRole}><Briefcase size={11} />{p.role}</span>
              </div>
              <span className={page.actRow}>
                <button type="button" className={page.actBtn}
                        onClick={() => notReady('Şifre değiştirme')}>
                  <KeyRound size={13} /> Şifre Değiştir
                </button>
                <button type="button" className={`${page.actBtn} ${page.actPrimary}`}
                        onClick={() => notReady('Destek talebi')}>
                  <LifeBuoy size={13} /> Destek Talebi
                </button>
              </span>
            </div>
            <div className={page.userMeta}>
              <span className={page.metaItem}>
                <span className={page.metaLabel}><Hash size={9} /> Cari Kodu</span>
                <span className={page.metaValue}>{p.customerCode}</span>
              </span>
              <span className={page.metaItem}>
                <span className={page.metaLabel}><Building2 size={9} /> Bayi Ünvanı</span>
                <span className={page.metaValue}>{p.companyTitle}</span>
              </span>
            </div>
          </section>
        </div>

        {/* kisisel bilgiler */}
        <section className={page.card}>
          <div className={page.cardHead}>
            <span className={page.cardBar} />
            <h2 className={page.cardTitle}>Kişisel Bilgiler</h2>
          </div>
          <div className={page.infoGrid}>
            {p.fields.map((f) => (
              <div key={f.key} className={page.infoItem}>
                <span className={page.infoLabel}>{f.label}</span>
                <span className={page.infoValue}>
                  {!f.value ? (
                    <span className={page.infoEmpty}>—</span>
                  ) : f.type === 'email' ? (
                    <a className={page.infoLink} href={`mailto:${f.value}`}>{f.value}</a>
                  ) : f.type === 'tel' ? (
                    <a className={page.infoLink} href={`tel:${f.value}`}>{f.value}</a>
                  ) : f.value}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* hesap hareketleri */}
        <section className={page.card}>
          <div className={page.cardHead}>
            <span className={page.cardBar} />
            <h2 className={page.cardTitle}>Hesap Hareketleri</h2>
          </div>

          <div className={page.sumRow}>
            <div className={`${page.sumBox} ${page.sumDebit}`}>
              <span className={`${page.sumIcon} ${page.sumIconDebit}`}><TrendingDown size={16} /></span>
              <span className={page.sumText}>
                <span className={`${page.sumLabel} ${page.sumLabelDebit}`}>Toplam Borç</span>
                <span className={`${page.sumValue} ${page.sumValueDebit}`}>{formatTRY(totals.debit)}</span>
              </span>
            </div>
            <div className={`${page.sumBox} ${page.sumCredit}`}>
              <span className={`${page.sumIcon} ${page.sumIconCredit}`}><TrendingUp size={16} /></span>
              <span className={page.sumText}>
                <span className={`${page.sumLabel} ${page.sumLabelCredit}`}>Toplam Alacak</span>
                <span className={`${page.sumValue} ${page.sumValueCredit}`}>{formatTRY(totals.credit)}</span>
              </span>
            </div>
          </div>

          <div className={page.toolbar}>
            <span className={page.searchBox}>
              <span className={page.searchIcon}><Search size={15} /></span>
              <input
                className={page.searchInput} type="text" value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Fiş no, açıklama veya tutar ile ara..."
                aria-label="Hesap hareketlerinde ara"
              />
              {term && (
                <button type="button" className={page.searchClear}
                        onClick={() => setTerm('')} aria-label="Aramayı temizle">
                  <X size={13} />
                </button>
              )}
            </span>

            <button type="button" className={`${page.expBtn} ${page.expPdf}`} onClick={() => notReady('PDF')}>
              <FileDown size={14} /> PDF
            </button>
            <button type="button" className={`${page.expBtn} ${page.expXls}`} onClick={() => notReady('Excel')}>
              <FileSpreadsheet size={14} /> Excel
            </button>

            <span className={page.dateGroup}>
              <span className={page.dateField}>
                <span className={page.dateLabel}>Başlangıç</span>
                <input className={page.dateInput} type="date" value={from}
                       onChange={(e) => setFrom(e.target.value)} aria-label="Başlangıç tarihi" />
              </span>
              <span className={page.dateDash}>—</span>
              <span className={page.dateField}>
                <span className={page.dateLabel}>Bitiş</span>
                <input className={page.dateInput} type="date" value={to}
                       onChange={(e) => setTo(e.target.value)} aria-label="Bitiş tarihi" />
              </span>
            </span>

            <span className={page.tbCount}><b>{formatNumber(hareket.total)}</b> kayıt</span>
          </div>

          {hareket.loading ? (
            <div className={page.empty}>
              <div>Hesap hareketleri yükleniyor…</div>
            </div>
          ) : hareket.error ? (
            <div className={page.empty}>
              <span className={page.emptyIcon}><Inbox size={30} strokeWidth={1.3} /></span>
              <div>{hareket.error}</div>
            </div>
          ) : rows.length === 0 ? (
            <div className={page.empty}>
              <span className={page.emptyIcon}><Inbox size={30} strokeWidth={1.3} /></span>
              <div>Kayıt bulunamadı.</div>
            </div>
          ) : (
            <div className={page.scroll}>
              <table className={page.table}>
                <colgroup>
                  <col className={page.colDate} />
                  <col className={page.colDoc} />
                  <col className={page.colType} />
                  <col className={page.colDesc} />
                  <col className={page.colAmt} />
                  <col className={page.colAmt} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Fiş No</th>
                    <th>Fiş Türü</th>
                    <th>Açıklama</th>
                    <th className={page.right}>Borç</th>
                    <th className={page.right}>Alacak</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id} className={page.row}>
                      <td className={page.dateCell}>{fmtDate(e.date)}</td>
                      <td><span className={page.docNo}>{e.docNo}</span></td>
                      <td><span className={page.docType}>{e.type}</span></td>
                      <td className={page.desc}>{e.description}</td>
                      <td className={page.right}>
                        <span className={`${page.debit} ${!e.debit ? page.zero : ''}`}>{formatTRY(e.debit)}</span>
                      </td>
                      <td className={page.right}>
                        <span className={`${page.credit} ${!e.credit ? page.zero : ''}`}>{formatTRY(e.credit)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* mobil: tablo yerine kart listesi */}
          {!hareket.loading && !hareket.error && rows.length > 0 && (
            <div className={page.mvList}>
              {rows.map((e) => (
                <article key={e.id} className={page.mvCard}>
                  <div className={page.mvTop}>
                    <span className={page.mvDate}>{fmtDate(e.date)}</span>
                    <span className={page.docType}>{e.type}</span>
                  </div>
                  {e.description && <p className={page.mvDesc}>{e.description}</p>}
                  <div className={page.mvBottom}>
                    <span className={page.mvDoc}>{e.docNo || '—'}</span>
                    <span className={page.mvAmounts}>
                      {e.debit > 0 && (
                        <span className={page.mvAmt}>
                          <span className={page.mvAmtLabel}>Borç</span>
                          <span className={page.debit}>{formatTRY(e.debit)}</span>
                        </span>
                      )}
                      {e.credit > 0 && (
                        <span className={page.mvAmt}>
                          <span className={page.mvAmtLabel}>Alacak</span>
                          <span className={page.credit}>{formatTRY(e.credit)}</span>
                        </span>
                      )}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}

          {hareket.totalPages > 1 && (
            <div className={page.pager}>
              <button type="button" className={page.pagerBtn}
                      onClick={() => setSayfa((s) => Math.max(s - 1, 1))}
                      disabled={hareket.page <= 1}>
                Önceki
              </button>
              <span className={page.pagerInfo}>
                Sayfa <b>{hareket.page}</b> / {hareket.totalPages}
              </span>
              <button type="button" className={page.pagerBtn}
                      onClick={() => setSayfa((s) => Math.min(s + 1, hareket.totalPages))}
                      disabled={hareket.page >= hareket.totalPages}>
                Sonraki
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
